import React, { useEffect, useRef, useState } from "react";
import CustomButton from "src/components/CustomButton";
import {
  Box,
  Divider,
  Grid,
  TextField,
  Tooltip,
  Typography,
  Button,
  IconButton,
  debounce,
  TextareaAutosize,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import {
  AddCircleOutline,
  DeleteOutline,
  PlayArrow,
  Stop,
} from "@mui/icons-material";
import backButton from "../../Images/backButton.svg";
import {
  ADD_MUST_DO_THINGS,
  UPDATE_MUST_DO_THINGS,
} from "src/graphql/mutations";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { uploadAudio, uploadImage } from "src/components/Common/Utils";
import {
  BusinessOptions,
  EventsOptions,
  ExplorePlaceOptions,
  ImageItem,
  mustDoThingsPageProps,
} from "./mustDoThings";
import Select, { components } from "react-select";
import {
  GET_BUSINESS,
  GET_MUST_DO_THINGS_ID,
  GET_POPULAR_LOCATIONS,
  GET_EVENTS,
} from "src/graphql/query";

const MustDoThingsModal: React.FC<mustDoThingsPageProps> = ({
  refetchMustDoThings,
  handleClose,
  initialMustDoThingsObj = {
    _id: "",
    title: "",
    slug: "",
    description: "",
    type: "",
    audio: "",
    isDeleted: false,
    images: [], // Should be an array of ImageItem objects
    isNews: false,
  },
  mustDoThingsObj = {
    _id: "",
    title: "",
    slug: "",
    description: "",
    type: "",
    audio: "",
    isDeleted: false,
    images: [], // array of ImageItem
    isNews: false,
  },
  setMustDoThingsObj = () => {},
  isEditMustDoThings = false,
}) => {
  const [addMustDoThing] = useMutation(ADD_MUST_DO_THINGS);
  const [updateMustDoThing] = useMutation(UPDATE_MUST_DO_THINGS);
  const imageRef = useRef<HTMLInputElement | null>(null);
  const [desktopUrl, setDesktopUrl] = useState<string>("");
  const [images, setImages] = useState<ImageItem[]>([
    {
      image: "",
      businessId: "",
      exploreId: "",
      eventsId: "",
      imagePreview: "",
      businessOptions: [],
      placeOptions: [],
      eventsOptions: [],
    },
  ]);
  const [businessSearchTerm, setBusinessSearchTerm] = useState("");
  const [placeSearchTerm, setPlaceSearchTerm] = useState("");
  const [eventSearchTerm, setEventSearchTerm] = useState("");
  const [businessOptions, setBusinessOptions] = useState<BusinessOptions[]>([]);
  const [placeOptions, setPlaceOptions] = useState<ExplorePlaceOptions[]>([]);
  const [eventsOptions, setEventsOptions] = useState<EventsOptions[]>([]);
  const audioRef = useRef<HTMLInputElement | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioPreview, setAudioPreview] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const CustomOption = ({ children, ...props }: any) => {
    const labelParts = children.split("\n"); // Split the label by newline
    return (
      <components.Option {...props}>
        {labelParts.map((part: string, index: number) => (
          <Box key={index} sx={{ display: "block" }}>
            {part}
          </Box>
        ))}
      </components.Option>
    );
  };

  const { data: mustDoThingsData } = useQuery(GET_MUST_DO_THINGS_ID, {
    variables: { _id: mustDoThingsObj?._id },
  });

  const [getBusinesses, { data: businessData, loading: businessloading }] =
    useLazyQuery(GET_BUSINESS, {
      variables: { search: businessSearchTerm },
      fetchPolicy: "network-only",
    });
  const [getExplorePlace, { data: placeData, loading: placeloading }] =
    useLazyQuery(GET_POPULAR_LOCATIONS, {
      variables: { search: placeSearchTerm },
      fetchPolicy: "network-only",
    });
  const [getEvents, { data: eventData, loading: eventloading }] = useLazyQuery(
    GET_EVENTS,
    {
      variables: { search: eventSearchTerm },
      fetchPolicy: "network-only",
    }
  );

  const handleSearchBusiness = debounce((term: string, index: number) => {
    if (term.length >= 3) {
      getBusinesses({
        variables: { search: term },
        onCompleted: (data) => {
          if (data?.getBusinessProfilesAll?.data) {
            const newOptions = data.getBusinessProfilesAll.data.map(
              (business: any) => ({
                value: business?._id,
                label: `${business?.businessName} (${business?.pinCodeDetails})\n${business?.slug}`,
              })
            );

            const updatedImages = [...images];
            updatedImages[index] = {
              ...updatedImages[index],
              businessOptions: newOptions,
            };
            setImages(updatedImages);
          }
        },
      });
    } else {
      const updatedImages = [...images];
      updatedImages[index] = {
        ...updatedImages[index],
        businessOptions: [],
      };
      setImages(updatedImages);
    }
  }, 300);

  const handleSearchPlace = debounce((term: string, index: number) => {
    if (term.length >= 3) {
      getExplorePlace({
        variables: { search: term },
        onCompleted: (data) => {
          if (data?.getPopularLocations?.data) {
            const newOptions = data.getPopularLocations.data.map(
              (place: any) => ({
                value: place?._id,
                label: `${place?.title}`,
              })
            );

            const updatedImages = [...images];
            updatedImages[index] = {
              ...updatedImages[index],
              placeOptions: newOptions,
            };
            setImages(updatedImages);
          }
        },
      });
    } else {
      const updatedImages = [...images];
      updatedImages[index] = {
        ...updatedImages[index],
        placeOptions: [],
      };
      setImages(updatedImages);
    }
  }, 300);

  const handleSearchEvent = debounce((term: string, index: number) => {
    if (term.length >= 3) {
      getEvents({
        variables: { search: term },
        onCompleted: (data) => {
          if (data?.getEventsAll?.data) {
            const newOptions = data.getEventsAll.data.map((event: any) => ({
              value: event?._id,
              label: `${event?.title}`,
            }));

            const updatedImages = [...images];
            updatedImages[index] = {
              ...updatedImages[index],
              eventsOptions: newOptions,
            };
            setImages(updatedImages);
          }
        },
      });
    } else {
      const updatedImages = [...images];
      updatedImages[index] = {
        ...updatedImages[index],
        eventsOptions: [],
      };
      setImages(updatedImages);
    }
  }, 300);

  useEffect(() => {
    if (
      businessData &&
      businessData?.getBusinessProfilesAll &&
      businessData?.getBusinessProfilesAll?.data
    ) {
      setBusinessOptions(
        businessData?.getBusinessProfilesAll?.data?.map((business: any) => ({
          value: business?._id,
          // label: `${business?.businessName}`,
          label: `${business?.businessName} (${business?.pinCodeDetails})\n${business?.slug}`,
        }))
      );
    }
  }, [businessData]);

  useEffect(() => {
    if (
      placeData &&
      placeData?.getPopularLocations &&
      placeData?.getPopularLocations?.data
    ) {
      setPlaceOptions(
        placeData?.getPopularLocations?.data?.map((place: any) => ({
          value: place?._id,
          label: `${place?.title}`,
        }))
      );
    }
  }, [placeData]);

  useEffect(() => {
    if (eventData && eventData?.getEventsAll && eventData?.getEventsAll?.data) {
      setEventsOptions(
        eventData?.getEventsAll?.data?.map((event: any) => ({
          value: event?._id,
          label: `${event?.title}`,
        }))
      );
    }
  }, [eventData]);

  useEffect(() => {
    if (
      isEditMustDoThings &&
      mustDoThingsObj?.images &&
      mustDoThingsData?.getMustDoThing?.data
    ) {
      const initialImages = mustDoThingsObj.images.map((img: any) => ({
        ...img,
        imagePreview: img?.image,
        image: img?.image,
        businessId: img?.businessId || "",
        exploreId: img?.exploreId || "",
        eventsId: img?.eventsId || "",
        businessOptions: img?.businessId
          ? [
              {
                value: img.businessId,
                label: `${img.businessName} (${img.businessArea} ${img.businessSlug})`,
              },
            ]
          : [],
        placeOptions: img?.exploreId
          ? [{ value: img.exploreId, label: img.exploreName }]
          : [],
        eventsOptions: img?.eventsId
          ? [{ value: img.eventsId, label: img.eventsName }]
          : [],
      }));
      setImages(initialImages);
    } else {
      setImages([
        {
          image: "",
          businessId: "",
          exploreId: "",
          eventsId: "",
          imagePreview: "",
          businessOptions: [],
          placeOptions: [],
          eventsOptions: [],
        },
      ]);
    }
  }, [isEditMustDoThings, mustDoThingsObj?._id]);

  const handleBusinessChange = (selectedOption: any | null, index: number) => {
    const updatedImages = [...images];
    updatedImages[index] = {
      ...updatedImages[index],
      businessId: selectedOption?.value || "",
    };
    setImages(updatedImages);
  };

  const handlePlaceChange = (selectedOption: any | null, index: number) => {
    const updatedImages = [...images];
    updatedImages[index] = {
      ...updatedImages[index],
      exploreId: selectedOption?.value || "",
    };
    setImages(updatedImages);
  };

  const handleEventChange = (selectedOption: any | null, index: number) => {
    const updatedImages = [...images];
    updatedImages[index] = {
      ...updatedImages[index],
      eventsId: selectedOption?.value || "",
    };
    setImages(updatedImages);
  };

  useEffect(() => {
    const title = mustDoThingsObj?.title || "";
    // lower-case + trim + replace spaces (and any non-word) with hyphens
    const generatedSlug = title
      ?.trim()
      ?.toLowerCase()
      ?.replace(/[^\w\s-]/g, "") // strip out punctuation
      ?.replace(/\s+/g, "-"); // spaces → hyphens

    setMustDoThingsObj((prev: any) => ({
      ...prev,
      slug: generatedSlug,
    }));
  }, [mustDoThingsObj?.title]);

  const handleImageChange = (
    index: number,
    field: keyof ImageItem,
    value: string | null
  ) => {
    const updatedImages = [...images];

    // For text fields and URL inputs
    updatedImages[index][field] = value as any;

    setImages(updatedImages);
  };

  // Handle image upload for the images array
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const res = await uploadImage(file, "mustDoThings", `image_${index}`);
      handleImageChange(index, "image", res?.uploadImage?.url);
      handleImageChange(index, "imagePreview", res?.uploadImage?.url);
    } catch (error) {
      toast.error("Error uploading image123");
    }
  };

  // Add new image row
  const handleAddImage = () => {
    setImages([
      ...images,
      {
        image: "",
        businessId: "",
        exploreId: "",
        eventsId: "",
        imagePreview: "",
        businessOptions: [],
        placeOptions: [],
        eventsOptions: [],
      },
    ]);
  };

  // Remove image row
  const handleRemoveImage = (index: number) => {
    if (images.length > 1) {
      const updatedImages = images.filter((_, i) => i !== index);
      setImages(updatedImages);
    }
  };

  const handleAudioUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if the file is an audio file
    if (!file.type.startsWith("audio/")) {
      toast.error("Please upload an audio file");
      return;
    }

    try {
      setLoading(true);
      const res = await uploadAudio(
        file,
        "mustDoThingsAudio",
        mustDoThingsObj?.title
      );
      console.log("res", res);
      setMustDoThingsObj({
        ...mustDoThingsObj,
        audio: res?.uploadAudio?.url || "",
      });
      setAudioPreview(res?.uploadAudio?.url || "");
      toast.success("Audio uploaded successfully");
    } catch (error) {
      toast.error("Error uploading audio");
    } finally {
      setLoading(false);
    }
  };

  // Handle audio playback
  const toggleAudioPlayback = () => {
    if (audioPlayerRef.current) {
      if (isPlaying) {
        audioPlayerRef.current.pause();
      } else {
        audioPlayerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const missingFields: { field: string; message: string }[] = [];

    if (!mustDoThingsObj?.title)
      missingFields.push({ field: "title", message: "Title is required" });
    if (!mustDoThingsObj?.type)
      missingFields.push({ field: "type", message: "Type is required" });
    images.forEach((img, idx) => {
      if (!img.image) {
        missingFields.push({
          field: `image_${idx}`,
          message: `Image is required for item ${idx + 1}`,
        });
      }
    });
    if (missingFields.length > 0) {
      missingFields.forEach((item, index) => {
        setTimeout(() => {
          toast.error(item.message);
        }); // Staggering the toasts by 300ms
      });
      return;
    }
    console.log("isEditMustDoThings", isEditMustDoThings);

    console.log("Current audio value:", mustDoThingsObj?.audio);
    // Add validation for audio if it's required
    if (!mustDoThingsObj?.audio) {
      missingFields.push({ field: "audio", message: "Audio is required" });
    }
    if (missingFields.length > 0) {
      missingFields.forEach((item, index) => {
        setTimeout(() => {
          toast.error(item.message);
        });
      });
      return;
    }

    try {
      const uploadedImages = await Promise.all(
        images.map(async (img) => {
          // If image is a File object, upload it
          // if (img.image as any) {
          //   const res = await uploadImage(
          //     img.image,
          //     "mustDoThings",
          //     `image_${img?.businessId || img?.exploreId || "item"}`
          //   );
          //   return {
          //     ...img,
          //     image: res?.uploadImage?.url || "",
          //   };
          // }
          return img;
        })
      );

      const cleanedImages = uploadedImages.map((img) => {
        const cleanedImage: any = {
          image: typeof img.image === "string" ? img.image : "",
        };

        // Only include businessId if it has a value
        if (img.businessId) {
          cleanedImage.businessId = img.businessId;
        }

        // Only include exploreId if it has a value
        if (img.exploreId) {
          cleanedImage.exploreId = img.exploreId;
        }

        if (img.eventsId) {
          cleanedImage.eventsId = img.eventsId;
        }

        return cleanedImage;
      });

      // Update the MustDoThings object with the new MustDoThings URL
      const updatedMustDoThingsObj = {
        ...mustDoThingsObj,
        images: cleanedImages,
      };

      console.log("updatedMustDoThingsObj", updatedMustDoThingsObj);

      // Determine whether to create a new MustDoThings or update an existing one
      const { data } = isEditMustDoThings
        ? await updateMustDoThing({
            variables: {
              _id: updatedMustDoThingsObj?._id,
              input: {
                title: updatedMustDoThingsObj?.title,
                isNews: updatedMustDoThingsObj?.isNews || false,
                audio: updatedMustDoThingsObj?.audio,
                // slug: updatedMustDoThingsObj?.slug,
                description: updatedMustDoThingsObj?.description,
                type: updatedMustDoThingsObj?.type,
                images: updatedMustDoThingsObj?.images,
              },
            },
          })
        : await addMustDoThing({
            variables: {
              input: {
                title: updatedMustDoThingsObj?.title,
                isNews: updatedMustDoThingsObj?.isNews || false,
                audio: updatedMustDoThingsObj?.audio,
                // slug: updatedMustDoThingsObj?.slug,
                description: updatedMustDoThingsObj?.description,
                type: updatedMustDoThingsObj?.type,
                images: updatedMustDoThingsObj?.images,
              },
            },
          });

      // Refetch MustDoThings to update the list
      refetchMustDoThings();
      console.log(" data?.updateMustDoThing", data?.updateMustDoThing);
      // Provide feedback to the user
      if (
        data?.addMustDoThing?.statusCode === 200 ||
        data?.updateMustDoThing?.statusCode === 200
      ) {
        toast.success(
          data?.addMustDoThing?.message ||
            data?.updateMustDoThing?.message ||
            "MustDoThings saved successfully"
        );
        setMustDoThingsObj(initialMustDoThingsObj);
        handleClose();
      } else {
        toast.error(
          data?.addMustDoThing?.message ||
            data?.updateMustDoThing?.message ||
            "Error saving MustDoThings"
        );
      }
    } catch (error: any) {
      toast.error(error?.message || "An unexpected error occurred.");
    }
  };

  useEffect(() => {
    if (isEditMustDoThings && mustDoThingsObj) {
      // setMustDoThingsObj(mustDoThingsObj);
      setMustDoThingsObj({
        ...mustDoThingsObj,
      });
      if (mustDoThingsObj.audio) {
        setAudioPreview(mustDoThingsObj.audio);
      }
    }
  }, [isEditMustDoThings, mustDoThingsObj]);

  return (
    <Box p={2}>
      <Box style={{ display: "flex", alignItems: "center" }}>
        <Tooltip title="Back">
          <img
            src={backButton}
            alt="backButton"
            height={25}
            width={25}
            style={{ marginRight: "12px", cursor: "pointer" }}
            onClick={handleClose}
          />
        </Tooltip>
        <Typography
          variant="h4"
          gutterBottom
          style={{ color: "#00C5B9" }}
          marginTop={2}
        >
          {isEditMustDoThings ? "Update MustDoThings" : "Create MustDoThings"}
        </Typography>
      </Box>

      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="title"
              margin="dense"
              label="Title"
              type="text"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={mustDoThingsObj?.title || ""}
              onChange={(e) => {
                const newValue = e.target.value;
                if (newValue?.length <= 30) {
                  setMustDoThingsObj({ ...mustDoThingsObj, title: newValue });
                }
              }}
              helperText={`${(mustDoThingsObj?.title || "")?.length}/30`}
              error={(mustDoThingsObj?.title || "")?.length > 30}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Select
              value={
                mustDoThingsObj?.type
                  ? {
                      value: mustDoThingsObj?.type,
                      label: mustDoThingsObj?.type,
                    }
                  : null
              }
              placeholder="Select Type"
              onChange={(selectedOption) => {
                setMustDoThingsObj((prev: any) => ({
                  ...prev,
                  type: selectedOption?.value || "",
                }));
              }}
              options={[
                { value: "MUSTDOTHINGS", label: "Must Do Things" },
                { value: "OTHERS", label: "Others" },
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="slug"
              margin="dense"
              label="Slug"
              type="text"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={mustDoThingsObj?.slug ?? ""}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={mustDoThingsObj?.isNews || false}
                  onChange={(e) =>
                    setMustDoThingsObj({
                      ...mustDoThingsObj,
                      isNews: e.target.checked,
                    })
                  }
                  color="primary"
                />
              }
              label="Is News"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">
              <strong style={{ color: "#333" }}>Description </strong>
            </Typography>
            <TextareaAutosize
              minRows={4}
              style={{ width: "100%", padding: "8px", fontSize: "16px" }}
              value={mustDoThingsObj?.description || ""}
              onChange={(e) => {
                const newValue = e.target.value;
                if (newValue?.length <= 90) {
                  setMustDoThingsObj({
                    ...mustDoThingsObj,
                    description: newValue,
                  });
                }
              }}
            />
            <Typography
              variant="caption"
              style={{ display: "block", textAlign: "right" }}
            >
              {(mustDoThingsObj?.description || "").length}/90
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ py: 1 }}>
              Audio
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <input
                type="file"
                accept="audio/*"
                onChange={handleAudioUpload}
                style={{ display: "none" }}
                id="audio-upload"
                ref={audioRef}
                disabled={!!mustDoThingsObj?.audio}
              />
              <label htmlFor="audio-upload">
                <CustomButton
                  variant="outlined"
                  component="span"
                  disabled={!!mustDoThingsObj?.audio}
                >
                  Upload Audio
                </CustomButton>
              </label>
              {loading && (
                <Box display="flex" justifyContent="center" marginTop={2}>
                  <CircularProgress />
                </Box>
              )}
              {(audioPreview || mustDoThingsObj?.audio) && (
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton onClick={toggleAudioPlayback}>
                    {isPlaying ? (
                      <Stop color="error" />
                    ) : (
                      <PlayArrow color="primary" />
                    )}
                  </IconButton>
                  <audio
                    ref={audioPlayerRef}
                    src={audioPreview || mustDoThingsObj?.audio}
                    onEnded={() => setIsPlaying(false)}
                    style={{ display: "none" }}
                  />
                  <Typography variant="body2">Audio file ready</Typography>
                  <IconButton
                    color="error"
                    onClick={() => {
                      setMustDoThingsObj({ ...mustDoThingsObj, audio: "" });
                      setAudioPreview("");
                      if (audioRef.current) audioRef.current.value = "";
                      if (audioPlayerRef.current) {
                        audioPlayerRef.current.pause();
                        setIsPlaying(false);
                      }
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ py: 3 }}>
              Images
            </Typography>
            <Grid container spacing={2}>
              {images.map((imageItem, index) => (
                <Grid item xs={12} sm={6}>
                  <Box
                    key={index}
                    mb={4}
                    p={2}
                    border={1}
                    borderRadius={2}
                    borderColor="divider"
                  >
                    <Grid container spacing={2}>
                      {/* Image Upload */}
                      <Grid item xs={12}>
                        <Typography variant="body1">Image:</Typography>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, index)}
                          style={{ display: "none" }}
                          id={`image-upload-${index}`}
                        />
                        <label htmlFor={`image-upload-${index}`}>
                          <CustomButton variant="outlined" component="span">
                            Upload Image
                          </CustomButton>
                        </label>
                        {imageItem.imagePreview && (
                          <Box mt={2} position="relative">
                            <img
                              src={imageItem.imagePreview}
                              alt={`Preview ${index}`}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "200px",
                                objectFit: "contain",
                              }}
                            />
                            <CustomButton
                              variant="outlined"
                              color="warning"
                              style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                zIndex: 1,
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                minWidth: "0",
                                backgroundColor: "white",
                              }}
                              onClick={() => {
                                const updatedImages = [...images];
                                updatedImages[index] = {
                                  ...updatedImages[index],
                                  image: "",
                                  imagePreview: "",
                                };
                                setImages(updatedImages);
                              }}
                              aria-label="Remove image"
                            >
                              <Tooltip title="Remove">
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    borderRadius: "50%",
                                  }}
                                >
                                  &times;
                                </Box>
                              </Tooltip>
                            </CustomButton>
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Select
                          isClearable
                          placeholder={
                            businessloading ? "Loading..." : "Select a Business"
                          }
                          value={
                            imageItem?.businessOptions?.find(
                              (option: any) =>
                                option?.value === imageItem?.businessId
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            handleBusinessChange(selectedOption, index)
                          }
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === "input-change") {
                              if (value === "") {
                                const updatedImages = [...images];
                                updatedImages[index] = {
                                  ...updatedImages[index],
                                  businessOptions: [],
                                };
                                setImages(updatedImages);
                              } else {
                                handleSearchBusiness(value, index);
                              }
                            }
                          }}
                          options={imageItem.businessOptions}
                          components={{ Option: CustomOption }}
                        />
                      </Grid>

                      {/* Explore Fields */}
                      <Grid item xs={12} sm={6}>
                        <Select
                          isClearable
                          placeholder={
                            placeloading ? "Loading..." : "Select a Place "
                          }
                          value={
                            imageItem?.placeOptions?.find(
                              (option: any) =>
                                option?.value === imageItem?.exploreId
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            handlePlaceChange(selectedOption, index)
                          }
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === "input-change") {
                              if (value === "") {
                                const updatedImages = [...images];
                                updatedImages[index] = {
                                  ...updatedImages[index],
                                  placeOptions: [],
                                };
                                setImages(updatedImages);
                              } else {
                                handleSearchPlace(value, index);
                              }
                            }
                          }}
                          options={imageItem.placeOptions}
                        />
                      </Grid>

                      {/* Event Fields */}
                      <Grid item xs={12} sm={6}>
                        <Select
                          isClearable
                          placeholder={
                            eventloading ? "Loading..." : "Select a Event "
                          }
                          value={
                            imageItem?.eventsOptions?.find(
                              (option: any) =>
                                option?.value === imageItem?.eventsId
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            handleEventChange(selectedOption, index)
                          }
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === "input-change") {
                              if (value === "") {
                                const updatedImages = [...images];
                                updatedImages[index] = {
                                  ...updatedImages[index],
                                  eventsOptions: [],
                                };
                                setImages(updatedImages);
                              } else {
                                handleSearchEvent(value, index);
                              }
                            }
                          }}
                          options={imageItem.eventsOptions}
                        />
                      </Grid>

                      {/* Add/Remove Buttons */}
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end">
                          <IconButton color="primary" onClick={handleAddImage}>
                            <AddCircleOutline />
                          </IconButton>
                          {images.length > 1 && (
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveImage(index)}
                            >
                              <DeleteOutline />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Box marginTop={2} width={"100%"}>
            <CustomButton onClick={handleSubmit}>
              {isEditMustDoThings
                ? "Update Must Do Things"
                : "Save Must Do Things"}
            </CustomButton>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

export default MustDoThingsModal;
